import { useVirtualizer } from '@tanstack/react-virtual';
import Button from 'antd/lib/button';
import Empty from 'antd/lib/empty';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Typography from 'antd/lib/typography';
import React, { useCallback, useRef } from 'react';
import { PRIMARY_COLOR } from '../../../theme';
import { BaseFilterAccordion } from './BaseFilterAccordion';
import { useResizable } from './useResizable';

const RESIZE_HANDLE_HEIGHT = 6;
const MAX_WIDTH = 160; // Maximum width for the select box

interface PrimalSelectOption<T = number> {
    label: string;
    value: T;
}

interface BaseFilterSelectRadioProps<T = number> {
    options: PrimalSelectOption<T>[];
    value: T | null;
    onChange: (value: T | null) => void;
    label?: string;
    disabled?: boolean;
    loading?: boolean;
    showClear?: boolean;
    selectedCountInLabel?: boolean;
    height?: number;
    resizable?: boolean;
    minHeight?: number;
    maxHeight?: number;
    onHeightChange?: (height: number) => void;
}

export function BaseFilterRadioSelect<T = number>({
    options,
    value,
    onChange,
    label,
    disabled = false,
    loading = false,
    showClear = false,
    height = 300,
    resizable = true,
    minHeight = 200,
    maxHeight = 500,
    onHeightChange,
}: BaseFilterSelectRadioProps<T>) {
    const { height: currentHeight, isResizing, handleMouseDown } = useResizable(height, minHeight, maxHeight, onHeightChange);

    const handleRadioChange = useCallback(
        (receivedValue: T) => {
            if (receivedValue === value) {
                // If the same value is clicked, toggle it off
                onChange(null);
            } else {
                // Otherwise, set the new value
                onChange(receivedValue);
            }
        },
        [onChange, value]
    );

    const handleClear = useCallback(() => {
        onChange(null);
    }, [onChange]);

    let listHeight = currentHeight;

    return (
        <BaseFilterAccordion label={label} value={value} onClear={handleClear}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                    height: resizable ? currentHeight : height,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',

                        marginBottom: '8px',
                        padding: '0 8px',
                        gap: '4px',
                    }}
                >
                    {showClear && (
                        <Button type="text" size="small" onClick={handleClear} disabled={disabled} style={{ marginLeft: 'auto' }}>
                            Clear
                        </Button>
                    )}
                </div>

                <div style={{ flex: 1, overflow: 'hidden' }}>
                    {/* Loading */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Spin spinning tip="Loading..." />
                        </div>
                    ) : null}

                    {/* No options */}
                    {!loading && options.length === 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    ) : null}

                    {/* Options List */}
                    {!loading && options.length !== 0 && (
                        <VirtualizedOptionsList
                            height={listHeight}
                            options={options}
                            value={value}
                            disabled={disabled}
                            onRadioChange={handleRadioChange}
                        />
                    )}
                </div>

                {/* Resize Handle */}
                {resizable && (
                    <div
                        onMouseDown={handleMouseDown}
                        style={{
                            height: RESIZE_HANDLE_HEIGHT,
                            cursor: 'ns-resize',
                            backgroundColor: isResizing ? PRIMARY_COLOR : 'transparent',
                            borderTop: '1px solid #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s',
                            position: 'relative',
                        }}
                    >
                        <div
                            style={{
                                width: '20px',
                                height: '2px',
                                backgroundColor: isResizing ? '#ffffff' : '#bfbfbf',
                                borderRadius: '1px',
                                transition: 'background-color 0.2s',
                            }}
                        />
                    </div>
                )}
            </div>
        </BaseFilterAccordion>
    );
}

// Virtualized Options List Component
interface VirtualizedOptionsListProps<T = number> {
    height: number;
    options: PrimalSelectOption<T>[];
    value: T | null;
    disabled: boolean;
    onRadioChange: (value: T) => void;
}

function VirtualizedOptionsList<T = number>({ height, options, value, disabled, onRadioChange }: VirtualizedOptionsListProps<T>) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 34, // Adjust based on your option height
        overscan: 5, // Number of items to render outside the visible area
        useAnimationFrameWithResizeObserver: true, // Use animation frame for smoother scrolling
        paddingEnd: 8, // Add some padding to the end for better scrolling experience
    });

    return (
        <div
            ref={parentRef}
            style={{
                height,
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map(virtualItem => {
                    const option = options[virtualItem.index];

                    return (
                        <div
                            key={virtualItem.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start}px)`,
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                            }}
                        >
                            <Radio
                                disabled={disabled}
                                checked={value === option.value}
                                onClick={e => onRadioChange(option.value)}
                                value={option.value}
                            >
                                <Typography.Text
                                    ellipsis={{ tooltip: { title: option.label, placement: 'right' } }}
                                    style={{ fontSize: '12px', lineHeight: '20px', maxWidth: MAX_WIDTH }}
                                >
                                    {option.label}
                                </Typography.Text>
                            </Radio>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
